/**
 * Connected accounts — link/unlink social identities on the signed-in account
 * (src/docs/APPLE_HIDDEN_EMAIL_LINKING.md §6, card 4).
 *
 * This is the client half of the backend-brokered link. The browser never sees
 * a Supabase session: `POST /api/auth/identities/link` hands back only a
 * provider authorize URL, and the native path posts an Apple `id_token` the OS
 * sheet already produced. Everything token-shaped stays server-side.
 *
 * The backend answers every failure with a stable `code` plus copy that is
 * already user-facing, so callers should render `error.data.error` rather than
 * inventing a second set of messages. The one exception is the web redirect
 * coming back from GoTrue itself, which never passes through our API — that is
 * what {@link describeProviderRedirectError} is for.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/** Providers Settings can attach. Matches the backend's LinkableProvider. */
export type LinkableProvider = 'apple' | 'google';

export interface LinkedIdentity {
  identityId: string;
  provider: string;
  email: string | null;
  /** True for an Apple "Hide My Email" relay alias (§7). */
  isPrivateEmail: boolean;
  createdAt: string | null;
}

export interface ConnectedAccounts {
  identities: LinkedIdentity[];
  /** False when only one identity remains — Supabase refuses to remove it. */
  canUnlink: boolean;
}

export const connectedAccountsQueryKey = ['auth', 'connected-accounts'] as const;

export function useConnectedAccounts(enabled: boolean = true) {
  return useQuery<ConnectedAccounts>({
    queryKey: connectedAccountsQueryKey,
    queryFn: () => api.get<ConnectedAccounts>('/api/auth/identities'),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Start the web link. Resolves with the provider URL to navigate to, or null
 * when the provider was already attached (an idempotent success — there is
 * nothing to navigate to).
 */
export async function startProviderLink(
  provider: LinkableProvider,
  redirectPath: string
): Promise<string | null> {
  const response = await api.post<{ url: string | null; alreadyLinked: boolean }>(
    '/api/auth/identities/link',
    { provider, redirectPath }
  );
  return response.alreadyLinked ? null : response.url;
}

/** Native link: hand the provider `id_token` straight to the backend. */
export async function linkProviderWithIdToken(params: {
  provider: LinkableProvider;
  idToken: string;
  nonce?: string;
}): Promise<ConnectedAccounts & { alreadyLinked: boolean }> {
  return await api.post('/api/auth/identities/link-native', {
    provider: params.provider,
    id_token: params.idToken,
    nonce: params.nonce,
  });
}

export function useUnlinkIdentity() {
  const queryClient = useQueryClient();

  return useMutation<ConnectedAccounts, ApiError, string>({
    mutationFn: (identityId: string) =>
      api.delete<ConnectedAccounts>(`/api/auth/identities/${identityId}`),
    onSuccess: (data) => {
      queryClient.setQueryData(connectedAccountsQueryKey, data);
    },
  });
}

/**
 * How a user moves a provider off the account that currently holds it.
 *
 * Mirrors IDENTITY_MOVE_STEPS on the backend; duplicated rather than fetched
 * because the web redirect path never touches our API and so has no response
 * to read them from. The backend copy is authoritative — keep them in step.
 *
 * The reason this is a recovery and not a dead end: a provider identity is a
 * key, not the account. Moving Apple off the duplicate leaves that account and
 * all its data intact, still reachable by email and password.
 */
export const IDENTITY_MOVE_STEPS: readonly string[] = [
  'Sign in to that other account (use the provider you are trying to connect).',
  "In Settings, change that account's email to one you can actually receive mail at, and confirm it. Do this before revoking anything — it is what lets the account keep working afterwards.",
  'Still in that account, open Connected Accounts and press Disconnect on the provider.',
  'Sign back in here, and press Connect again.',
];

/** The conflict a user can act on, as rendered by the Settings card. */
export interface IdentityConflict {
  message: string;
  steps: readonly string[];
}

/**
 * GoTrue's own failure copy for the web redirect path.
 *
 * When the provider bounces back through Supabase, a rejected link never
 * touches our API — GoTrue redirects straight to the return URL with
 * `error_code` on it. Without this the user would land on Settings with nothing
 * changed and no explanation, which is exactly the "clear error, not a 500"
 * requirement failing by a different route.
 */
export function describeProviderRedirectError(
  errorCode: string | null,
  errorDescription: string | null
): string {
  switch (errorCode) {
    case 'identity_already_exists':
      return 'That account is already connected to a different Trade Loop account. You can move it here — nothing on the other account is deleted, and it keeps working with its email and password.';
    case 'manual_linking_disabled':
      return 'Connecting accounts is temporarily unavailable. Please try again later.';
    case 'bad_oauth_state':
    case 'flow_state_expired':
    case 'flow_state_not_found':
      return 'That linking attempt expired before it finished. Please try again.';
    default:
      return errorDescription || 'We could not connect that account. Please try again.';
  }
}

/** Human label for a provider slug, including the ones we do not offer to link. */
export function providerLabel(provider: string): string {
  switch (provider) {
    case 'apple':
      return 'Apple';
    case 'google':
      return 'Google';
    case 'email':
      return 'Email & password';
    case 'phone':
      return 'Phone';
    default:
      return provider.charAt(0).toUpperCase() + provider.slice(1);
  }
}
